import React, { useState, useEffect, lazy, Suspense } from 'react'
import featureFlags from './featureFlags'
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Outlet, useNavigate } from 'react-router-dom'
import styled, { ThemeProvider } from 'styled-components'
import LanguageProvider, { useLocalization } from './contexts/Language/LanguageProvider'
import { KeyBindingsProvider } from './contexts/KeyBindings/KeyBindingsProvider'
import Navigation from './components/Navigation'
import BasketballLoader from './components/BasketballLoader'
import ShareButtons from './components/ShareButtons'
import Footer from './components/Footer'
import CookieBanner from './components/CookieBanner'
import { lightTheme, darkTheme } from './themes/themes'
import { createGlobalStyle } from 'styled-components'
import { useSpring, animated } from 'react-spring'
import Themes from './constants/Themes'
import { isServer, isPrerendered, currentOrigin, storage } from './utils/env'

const ShotClockPage = lazy(() => import('./components/ShotClockPage'))
const LandingPage = lazy(() => import('./components/LandingPage'))
const AboutUs = lazy(() => import('./components/AboutUs'))
const Instructions = lazy(() => import('./components/Instructions'))
const FIBAResources = lazy(() => import('./components/FIBAResources'))
const FAQ = lazy(() => import('./components/FAQ'))
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'))
const NotFound = lazy(() => import('./components/NotFound'))
const ReactionTrainingPage = lazy(() => import('./components/training/ReactionTrainingPage'))
const PlayPage = lazy(() => import('./components/PlayPage'))
const YouTubePage = lazy(() => import('./components/YouTubePage'))
const TermsOfService = lazy(() => import('./components/TermsOfService'))
const GuidesHub = lazy(() => import('./components/learn/GuidesHub'))
const ShotClockHistory = lazy(() => import('./components/learn/ShotClockHistory'))
const ShotClockRulesByLeague = lazy(() => import('./components/learn/ShotClockRulesByLeague'))
const FourteenSecondReset = lazy(() => import('./components/learn/FourteenSecondReset'))
const EightSecondRule = lazy(() => import('./components/learn/EightSecondRule'))
const OperatorMistakes = lazy(() => import('./components/learn/OperatorMistakes'))
const LastTwoMinutes = lazy(() => import('./components/learn/LastTwoMinutes'))

const NON_ENGLISH_LANGS = ['it', 'es', 'fr', 'el']

// Skip the layout fade-in when rendering to static HTML or hydrating it, so
// pre-rendered content is never shipped (or flashed) at opacity 0.
const INITIAL_OPACITY = isServer || isPrerendered ? 1 : 0

const ScrollToTop: React.FC = () => {
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])
  return null
}

const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const prevPath = React.useRef(location.pathname)

  useEffect(() => {
    if (location.pathname === prevPath.current) return
    prevPath.current = location.pathname
    setLoading(true)
    const id = setTimeout(() => setLoading(false), 1000)
    return () => clearTimeout(id)
  }, [location.pathname])

  return loading ? <BasketballLoader /> : <>{children}</>
}

interface PageContentProps {
  children: React.ReactNode
  title: string
}

const PageContent: React.FC<PageContentProps> = ({ children, title }) => {
  const location = useLocation()
  const currentUrl = currentOrigin() + location.pathname

  return (
    <ContentCard>
      {children}
      <ShareButtons url={currentUrl} title={title} />
    </ContentCard>
  )
}

const LANG_KEY = 'sc_lang'

// Layout for English routes — no URL lang prefix
const EnglishLayout = () => {
  const { changeLocale } = useLocalization()
  const navigate = useNavigate()
  const location = useLocation()
  const fadeIn = useSpring({ from: { opacity: INITIAL_OPACITY }, to: { opacity: 1 }, config: { duration: 800 } })

  useEffect(() => {
    const savedLang = storage.get(LANG_KEY)
    if (savedLang && savedLang !== 'en' && NON_ENGLISH_LANGS.includes(savedLang) && location.pathname === '/') {
      navigate(`/${savedLang}`, { replace: true })
    } else {
      changeLocale('en')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <animated.div style={fadeIn}><Outlet /></animated.div>
}

// Layout for non-English language routes — reads /:lang from URL
// Also handles /en/* by redirecting to the canonical English URL
const LangLayout = () => {
  const { lang } = useParams<{ lang: string }>()
  const { changeLocale } = useLocalization()
  const navigate = useNavigate()
  const location = useLocation()
  const fadeIn = useSpring({ from: { opacity: INITIAL_OPACITY }, to: { opacity: 1 }, config: { duration: 800 } })

  const isValidLang = lang === 'en' || (!!lang && NON_ENGLISH_LANGS.includes(lang))

  useEffect(() => {
    if (!isValidLang) return
    if (lang === 'en') {
      // Redirect /en/* → /* to keep English at canonical URLs
      const pagePath = location.pathname.slice('/en'.length) || '/'
      navigate(pagePath, { replace: true })
    } else if (lang && NON_ENGLISH_LANGS.includes(lang)) {
      changeLocale(lang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang])

  if (!isValidLang) return <NotFound />

  return <animated.div style={fadeIn}><Outlet /></animated.div>
}

const ClockShareButtons: React.FC<{ title: string; description: string }> = props => {
  const location = useLocation()
  return <ShareButtons url={currentOrigin() + location.pathname} {...props} />
}

const pageRoutes = (
  <>
    <Route index element={<LandingPage />} />
    <Route path="play" element={<PlayPage />} />
    <Route path="clock" element={
      <>
        <ShotClockPage />
        <ClockShareButtons
          title="Check out this Basketball Shot Clock App!"
          description="A professional basketball shot clock app for referees and players."
        />
      </>
    } />
    <Route path="about" element={
      <PageContent title="About Us - Basketball Shot Clock App">
        <AboutUs />
      </PageContent>
    } />
    <Route path="instructions" element={
      <PageContent title="Instructions - Basketball Shot Clock App">
        <Instructions />
      </PageContent>
    } />
    <Route path="fiba-resources" element={
      <PageContent title="FIBA Resources - Basketball Shot Clock App">
        <FIBAResources />
      </PageContent>
    } />
    <Route path="faq" element={
      <PageContent title="FAQ - Basketball Shot Clock App">
        <FAQ />
      </PageContent>
    } />
    <Route path="privacy-policy" element={
      <PageContent title="Privacy Policy - ShotClock Pro">
        <PrivacyPolicy />
      </PageContent>
    } />
    {featureFlags.reactionTraining && (
      <Route path="reaction-training" element={
        <PageContent title="Reaction Training - ShotClock Pro">
          <ReactionTrainingPage />
        </PageContent>
      } />
    )}
    <Route path="terms" element={
      <PageContent title="Terms of Service - ShotClock Pro">
        <TermsOfService />
      </PageContent>
    } />
    <Route path="learn" element={
      <PageContent title="Basketball Shot Clock Guides - ShotClock Pro">
        <GuidesHub />
      </PageContent>
    } />
    <Route path="learn/shot-clock-history" element={
      <PageContent title="The History of the 24-Second Shot Clock - ShotClock Pro">
        <ShotClockHistory />
      </PageContent>
    } />
    <Route path="learn/shot-clock-rules-by-league" element={
      <PageContent title="Shot Clock Rules Compared - ShotClock Pro">
        <ShotClockRulesByLeague />
      </PageContent>
    } />
    <Route path="learn/14-second-reset" element={
      <PageContent title="The 14-Second Reset Explained - ShotClock Pro">
        <FourteenSecondReset />
      </PageContent>
    } />
    <Route path="learn/eight-second-rule" element={
      <PageContent title="The 8-Second Rule and the Shot Clock - ShotClock Pro">
        <EightSecondRule />
      </PageContent>
    } />
    <Route path="learn/shot-clock-operator-mistakes" element={
      <PageContent title="10 Common Shot Clock Operator Mistakes - ShotClock Pro">
        <OperatorMistakes />
      </PageContent>
    } />
    <Route path="learn/last-two-minutes" element={
      <PageContent title="The Last Two Minutes - Clock Rules - ShotClock Pro">
        <LastTwoMinutes />
      </PageContent>
    } />
    {featureFlags.youtube && (
      <Route path="youtube" element={<YouTubePage />} />
    )}
  </>
)

const THEME_KEY = 'sc_theme'

// Everything below the router. The browser wraps it in BrowserRouter (App);
// the build-time pre-renderer wraps it in StaticRouter (src/ssr/entry-server.tsx).
export const AppShell = () => {
  // Pre-rendered HTML is always dark; hydrate with it, then apply the saved theme
  const [selectedTheme, setSelectedTheme] = useState<string>(
    () => (isPrerendered ? null : storage.get(THEME_KEY)) ?? Themes.Dark
  )

  useEffect(() => {
    const savedTheme = storage.get(THEME_KEY)
    if (isPrerendered && savedTheme) setSelectedTheme(savedTheme)
  }, [])
  const theme = selectedTheme === Themes.Light ? lightTheme : darkTheme

  const handleSetTheme = (t: string) => {
    storage.set(THEME_KEY, t)
    setSelectedTheme(t)
  }

  return (
    <ThemeProvider theme={theme}>
      <LanguageProvider>
        <GlobalStyle />
        <KeyBindingsProvider>
        <AppContainer>
          <Navigation currentTheme={selectedTheme} setTheme={handleSetTheme} />
          <PageWrapper>
            <MainContent>
              <Suspense fallback={<BasketballLoader />}>
                <ScrollToTop />
                <PageTransition>
                  <Routes>
                    {/* English — canonical URLs without lang prefix */}
                    <Route path="/" element={<EnglishLayout />}>
                      {pageRoutes}
                    </Route>

                    {/* Non-English languages + /en/* redirect */}
                    <Route path="/:lang" element={<LangLayout />}>
                      {pageRoutes}
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </PageTransition>
              </Suspense>
            </MainContent>
            <Footer />
          </PageWrapper>
          <CookieBanner />
        </AppContainer>
        </KeyBindingsProvider>
      </LanguageProvider>
    </ThemeProvider>
  )
}

const App = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <AppShell />
  </Router>
)

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
    background: ${props => props.theme.mainBackgroundColor};
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  /* Bootstrap base reset */
  p {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-weight: 500;
    line-height: 1.2;
  }

  ul, ol {
    margin-top: 0;
    margin-bottom: 1rem;
    padding-left: 2rem;
  }

  a {
    text-decoration: none;
  }

  img {
    max-width: 100%;
    vertical-align: middle;
    border-style: none;
  }

  button, input, select, textarea {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  hr {
    margin: 1rem 0;
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`

const PageWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 80px;
  min-height: calc(100vh - 80px);
`

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
`

const ContentCard = styled.div`
  background: ${props => props.theme.pageCard};
  border-radius: 16px;
  padding: 2rem;
  margin: 2rem auto;
  max-width: 1200px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${props => props.theme.cardBorder};
`

export default App
